import { useState, useMemo } from 'react';
import { PhoneCallRow } from '../../../types/supabase';
import { PhoneCallStatus } from './PhoneCallStatus';
import './PhoneCallView.scss';

interface PhoneCallViewProps {
  phoneCall: PhoneCallRow;
  isExpanded?: boolean;
  onToggle?: () => void;
  showToggle?: boolean;
}

interface TranscriptMessage {
  role: 'assistant' | 'user';
  text: string;
}

export function PhoneCallView({
  phoneCall,
  isExpanded = true,
  onToggle,
  showToggle = false
}: PhoneCallViewProps) {
  const [showTranscript, setShowTranscript] = useState(false);

  // Parse transcript into structured messages
  const parsedTranscript = useMemo(() => {
    if (!phoneCall.transcript) return [];

    const messages: TranscriptMessage[] = [];
    const lines = phoneCall.transcript.split('\n');
    let currentMessage: TranscriptMessage | null = null;

    for (const line of lines) {
      const trimmedLine = line.trim();
      if (!trimmedLine) continue;

      // Check if line starts with a role prefix
      if (trimmedLine.startsWith('Assistant:')) {
        // Save previous message if exists
        if (currentMessage) {
          messages.push(currentMessage);
        }
        // Start new assistant message
        currentMessage = {
          role: 'assistant',
          text: trimmedLine.substring('Assistant:'.length).trim()
        };
      } else if (trimmedLine.startsWith('User:')) {
        // Save previous message if exists
        if (currentMessage) {
          messages.push(currentMessage);
        }
        // Start new user message
        currentMessage = {
          role: 'user',
          text: trimmedLine.substring('User:'.length).trim()
        };
      } else if (trimmedLine.startsWith('Tool:')) {
        // Skip tool messages - don't display them
        continue;
      } else if (currentMessage) {
        // Continuation of current message
        currentMessage.text += '\n' + trimmedLine;
      }
    }

    // Don't forget to add the last message
    if (currentMessage) {
      messages.push(currentMessage);
    }

    return messages;
  }, [phoneCall.transcript]);

  const formatPhoneCallTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffInHours < 24 * 7) {
      return date.toLocaleDateString([], { weekday: 'short', hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    }
  };

  // Collapsed view
  if (!isExpanded && showToggle && onToggle) {
    return (
      <div className="phone-call-card collapsed message-user" onClick={onToggle}>
        <div className="phone-call-header">
          <div className="phone-call-icon">
            <i className="material-icons">phone</i>
            <i className="material-icons">arrow_outward</i>
          </div>
          <div className="phone-call-meta">
            <div className="phone-call-label">
              AI
            </div>
            <div className="phone-call-time">
              {formatPhoneCallTime(phoneCall.created_at)}
            </div>
          </div>
          <PhoneCallStatus status={phoneCall.status} />
        </div>
      </div>
    );
  }

  // Expanded view
  return (
    <div className="phone-call-card expanded message-user">
      <div className="phone-call-header">
        <div className="phone-call-icon">
          <i className="material-icons">phone</i>
          <i className="material-icons">arrow_outward</i>
        </div>
        <div className="phone-call-meta">
          <div className="phone-call-label">
            AI
          </div>
          <div className="phone-call-time">
            {formatPhoneCallTime(phoneCall.created_at)}
          </div>
        </div>
        <PhoneCallStatus status={phoneCall.status} />
        {showToggle && onToggle && (
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={onToggle}
            title="Collapse phone call"
          >
            <i className="material-icons">expand_less</i>
          </button>
        )}
      </div>

      <div className="phone-call-body">
        {/* Show analysis summary if available */}
        {phoneCall.analysis_summary && (
          <p className="text-muted" style={{ fontStyle: 'italic', marginBottom: '0.5rem' }}>
            {phoneCall.analysis_summary}
          </p>
        )}

        {/* Show/Hide transcript toggle */}
        {phoneCall.transcript && (
          <>
            <button
              className="btn btn-link p-0 text-decoration-underline"
              onClick={() => setShowTranscript(!showTranscript)}
              style={{ fontSize: 'inherit' }}
            >
              {showTranscript ? 'Hide transcript' : 'Show transcript'}
            </button>

            {showTranscript && (
              <div className="phone-call-transcript mt-2">
                {parsedTranscript.map((message, index) => (
                  <div
                    key={index}
                    className={`transcript-message ${message.role}`}
                    style={{
                      marginBottom: '0.75rem',
                      display: 'flex',
                      justifyContent: message.role === 'assistant' ? 'flex-start' : 'flex-end'
                    }}
                  >
                    <div
                      style={{
                        maxWidth: '80%',
                        padding: '0.5rem 0.75rem',
                        borderRadius: '0.5rem',
                        backgroundColor: message.role === 'assistant' ? '#e9ecef' : '#0d6efd',
                        color: message.role === 'assistant' ? '#000' : '#fff',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word'
                      }}
                    >
                      {message.text}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {phoneCall.error && (
        <div className="phone-call-error">
          <i className="material-icons">error</i>
          <span>{phoneCall.error}</span>
        </div>
      )}
    </div>
  );
}
